import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Checkbox,
  FormControlLabel,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { API_URL } from '../utils/apiConfig';

const PayChangeAcknowledgement = ({ open, onClose, onAcknowledged }) => {
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [payChanges, setPayChanges] = useState([]);
  const [acknowledged, setAcknowledged] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isMaryJane, setIsMaryJane] = useState(false);
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    if (open) {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          setIsMaryJane(payload?.email === 'maryjane@dailey.llc' && !payload?.isImpersonating);
          setUserRole(payload?.role || null);
        }
      } catch (_) {}
      fetchPendingNotifications();
    }
  }, [open]);

  const fetchPendingNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/notifications/pending`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
        setPayChanges(data.payChanges || []);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async () => {
    if (!acknowledged) {
      setError('Please check the box to acknowledge');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const notificationIds = notifications.map(n => n.id);
      const payChangeIds = payChanges.map(p => p.id);

      const response = await fetch(`${API_URL}/notifications/acknowledge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          notificationIds,
          payChangeIds
        })
      });

      if (response.ok) {
        if (typeof onAcknowledged === 'function') {
          onAcknowledged();
        } else {
          onClose();
        }
      } else {
        setError('Failed to acknowledge notifications');
      }
    } catch (error) {
      console.error('Failed to acknowledge:', error);
      setError('Failed to acknowledge notifications');
    } finally {
      setSubmitting(false);
    }
  };

  const hasMaryJaneOnlyItems = () => {
    // Employee changes that only Mary Jane can acknowledge
    const restrictedCategories = new Set(['tax_forms','banking','personal','forms','documents']);
    const restrictChange = payChanges.some(c => c.changeType === 'pay_update' || restrictedCategories.has(c.changeCategory));
    const restrictNotif = notifications.some(n => n.type === 'PAY_CHANGE');
    return restrictChange || restrictNotif;
  };

  // Don't show modal if no notifications
  if (!open || (!loading && payChanges.length === 0 && notifications.length === 0)) {
    return null;
  }

  return (
    <Dialog 
      open={open} 
      maxWidth="md" 
      fullWidth
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return;
        onClose();
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: '#fff3cd', 
        borderBottom: '2px solid #856404',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon sx={{ color: '#856404' }} />
          <Typography variant="h6" component="span">
            Important: Employee Changes Require Acknowledgement
          </Typography>
        </Box>
        <IconButton aria-label="Close" onClick={() => onClose()} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ mt: 2 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Typography variant="body1" gutterBottom>
              The following employee changes have been made and require your acknowledgement:
            </Typography>

            {payChanges.map((change, index) => {
              // Parse change details: support both stringified JSON and object payloads
              let details = {};
              try {
                const detailsSource = (change && (change.changeDetails ?? change.change_details ?? change.details)) || null;
                if (detailsSource) {
                  if (typeof detailsSource === 'string') {
                    details = JSON.parse(detailsSource);
                  } else if (typeof detailsSource === 'object') {
                    details = detailsSource;
                  }
                }
              } catch (_) {
                // leave details as {}
              }

              // Helpers to robustly extract and format values from mixed payloads
              const pick = (obj, keys, fallback = undefined) => {
                for (const k of keys) {
                  if (obj && obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k];
                }
                return fallback;
              };

              const formatRate = (val) => {
                if (val === undefined || val === null || val === '') return 'Not set';
                // If already a number-like string with currency, try to parse out digits
                let num = undefined;
                if (typeof val === 'number') {
                  num = val;
                } else if (typeof val === 'string') {
                  // Remove everything except digits and decimal
                  const cleaned = val.replace(/[^0-9.]/g, '');
                  const parsed = parseFloat(cleaned);
                  if (!isNaN(parsed)) num = parsed;
                }
                if (typeof num === 'number' && !isNaN(num)) {
                  return `$${num.toFixed(2)}/hr`;
                }
                // Fallback: if we cannot parse, return the original as-is
                // Ensure it includes $ and /hr if it looks like a bare number
                if (typeof val === 'string' && /^\d+(\.\d+)?$/.test(val)) {
                  return `$${Number(val).toFixed(2)}/hr`;
                }
                return String(val);
              };

              // Fallbacks when details is empty (e.g., prod may deserialize JSON -> object or store values at root)
              const fallbackFromRoot = () => {
                const rootOld = change && (change.oldValue ?? change.old_value ?? change.old);
                const rootNew = change && (change.newValue ?? change.new_value ?? change.new);
                return { rootOld, rootNew };
              };

              const tryParseJSON = (v) => {
                if (!v || typeof v !== 'string') return null;
                try {
                  return JSON.parse(v);
                } catch (_) { return null; }
              };

              return (
                <Paper key={change.id} variant="outlined" sx={{ p: 2, mb: 2, mt: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    {change.firstName} {change.lastName}
                  </Typography>
                  <Box sx={{ pl: 2 }}>
                    <Typography variant="body2" gutterBottom color="primary">
                      <strong>Change Type:</strong> {change.changeType.replace(/_/g, ' ').toUpperCase()}
                    </Typography>
                    
                    {/* Display based on change type */}
                    {change.changeType === 'pay_update' && (() => {
                      const oldPos = pick(details, ['old_position','oldPosition','previousPosition','from_position','position_from']);
                      const newPos = pick(details, ['new_position','newPosition','to_position','position_to']);

                      let oldRateRaw = pick(details, [
                        'old_pay_rate','oldPayRate','previousPayRate','from_pay_rate','payRateFrom','old_rate','oldRate','oldPay'
                      ]);
                      let newRateRaw = pick(details, [
                        'new_pay_rate','newPayRate','to_pay_rate','payRateTo','new_rate','newRate','newPay'
                      ]);

                      // If details didn’t provide anything, fallback to root-level values
                      if ((oldRateRaw === undefined && newRateRaw === undefined)) {
                        const { rootOld, rootNew } = fallbackFromRoot();
                        // If root values look like JSON objects, extract payRate keys
                        const oldObj = tryParseJSON(rootOld);
                        const newObj = tryParseJSON(rootNew);
                        if (oldObj && (oldObj.payRate !== undefined || oldObj.pay_rate !== undefined)) {
                          oldRateRaw = oldObj.payRate ?? oldObj.pay_rate;
                        } else if (rootOld !== undefined) {
                          oldRateRaw = rootOld;
                        }
                        if (newObj && (newObj.payRate !== undefined || newObj.pay_rate !== undefined)) {
                          newRateRaw = newObj.payRate ?? newObj.pay_rate;
                        } else if (rootNew !== undefined) {
                          newRateRaw = rootNew;
                        }
                      }

                      const oldRate = formatRate(oldRateRaw);
                      const newRate = formatRate(newRateRaw);

                      const showPosition = (oldPos !== undefined || newPos !== undefined) && (oldPos !== newPos);

                      return (
                        <>
                          {showPosition && (
                            <Typography variant="body2" gutterBottom>
                              <strong>Position:</strong> {oldPos ?? 'Not set'} → {newPos ?? 'Not set'}
                            </Typography>
                          )}
                          <Typography variant="body2" gutterBottom>
                            <strong>Pay Rate:</strong> {oldRate} → {newRate}
                          </Typography>
                          {details.change_note && (
                            <Typography variant="body2" gutterBottom>
                              <strong>Reason:</strong> {details.change_note}
                            </Typography>
                          )}
                        </>
                      );
                    })()}
                    
                    {change.changeType === 'w4_update' && (
                      <Typography variant="body2" gutterBottom>
                        W-4 tax withholding form has been updated
                      </Typography>
                    )}
                    
                    {change.changeType === 'personal_info' && (
                      <Typography variant="body2" gutterBottom>
                        {(() => {
                          const fmtDate = (v) => {
                            if (!v) return 'Not set';
                            try {
                              const d = new Date(v);
                              if (isNaN(d.getTime())) return String(v);
                              return d.toISOString().slice(0, 10);
                            } catch (_) {
                              return String(v);
                            }
                          };
                          const formatValue = (field, v) => field === 'dob' ? fmtDate(v) : (v ?? 'Not set');
                          const oldVal = formatValue(change.fieldName, change.oldValue);
                          const newVal = formatValue(change.fieldName, change.newValue);
                          return (
                            <>
                              <strong>{change.fieldName}:</strong> {oldVal} → {newVal}
                            </>
                          );
                        })()}
                      </Typography>
                    )}
                    
                    {change.changeType === 'emergency_contact' && (
                      <Typography variant="body2" gutterBottom>
                        Emergency contact information has been updated
                      </Typography>
                    )}
                    
                    {change.changeType === 'direct_deposit' && (
                      <Typography variant="body2" gutterBottom>
                        Direct deposit information has been updated
                        {details.bankName && ` (${details.bankName})`}
                      </Typography>
                    )}
                    
                    {/* Generic display for other change types */}
                    {!['pay_update', 'w4_update', 'personal_info', 'emergency_contact', 'direct_deposit'].includes(change.changeType) && (
                      <Typography variant="body2" gutterBottom>
                        {change.fieldName && (
                          <>
                            <strong>{change.fieldName}:</strong> {change.oldValue || 'Not set'} → {change.newValue}
                          </>
                        )}
                        {!change.fieldName && change.changeDetails && (
                          <>Details: {change.changeDetails}</>
                        )}
                      </Typography>
                    )}
                    
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      {(() => {
                        const actor = (change.changed_by_name || '').trim();
                        const safeActor = (!actor || /undefined/i.test(actor)) ? 'Employee Self-Service' : actor;
                        const when = change.created_at ? new Date(change.created_at).toLocaleDateString() : '';
                        return (
                          <>
                            <strong>Changed by:</strong> {safeActor}{when ? ` on ${when}` : ''}
                          </>
                        );
                      })()}
                    </Typography>
                  </Box>
                </Paper>
              );
            })}

            {notifications.map((notification, index) => (
              <Paper key={notification.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {notification.title}
                </Typography>
                <Typography variant="body2">
                  {notification.message}
                </Typography>
              </Paper>
            ))}

            <Divider sx={{ my: 2 }} />

            {isMaryJane && (
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={acknowledged} 
                    onChange={(e) => setAcknowledged(e.target.checked)}
                    color="primary"
                  />
                }
                label="I acknowledge that I have reviewed all employee changes listed above"
                sx={{ mt: 1 }}
              />
            )}

            {(userRole === 'manager') && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Acknowledgements are handled by Mary Jane. You can review items here.
              </Alert>
            )}

            {hasMaryJaneOnlyItems() && !isMaryJane && userRole !== 'manager' && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Requires Mary Jane: These changes can only be acknowledged by Mary Jane.
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
        <Button onClick={() => onClose()} color="inherit">
          Close
        </Button>
        {isMaryJane && (
          <Button
            onClick={handleAcknowledge}
            variant="contained"
            color="primary"
            disabled={!acknowledged || submitting || loading}
          >
            {submitting ? 'Processing...' : 'Acknowledge Changes'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PayChangeAcknowledgement;
